/* SPEC-145 — the tour picker.
 *
 * A modal listing the tours this user can actually walk (role-filtered), each
 * with a one-line "who it's for". Remembers the last-viewed step per tour so a
 * partly-walked tour offers to resume. Visible to every role — the tour list
 * self-filters, so an exec sees fewer steps but the same button. */
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { availableTours, visibleSteps } from '@/config/tours';
import { useTour } from './TourProvider';
import { TourGlyph } from './icons/tourGlyphs';

const LS_PREFIX = 'suryaiq_tour_';

function resumeStep(tourId: string, stepCount: number): number {
  try {
    const raw = localStorage.getItem(LS_PREFIX + tourId);
    const n = raw == null ? 0 : parseInt(raw, 10);
    // A saved final step means "finished" — no resume hint.
    return Number.isFinite(n) && n > 0 && n < stepCount - 1 ? n : 0;
  } catch {
    return 0;
  }
}

export function TourPicker() {
  const { pickerOpen, closePicker, startTour } = useTour();
  const { user } = useAuth();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pickerOpen, closePicker]);

  // SPEC-509: the picker opens from the rail's Walk-throughs button. Capture the opener ONCE
  // per open (this effect depends on pickerOpen alone, so a re-created closePicker cannot
  // re-capture the Close button), and on close return focus to it — unless a tour moved
  // focus on, or the opener left the page (a phone menu row): then the menu button.
  useEffect(() => {
    if (!pickerOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => {
      if (document.activeElement && document.activeElement !== document.body) return;
      const target = opener && opener.isConnected && opener !== document.body
        ? opener
        : document.querySelector<HTMLElement>('[data-mobile-menu-button]');
      target?.focus();
    };
  }, [pickerOpen]);

  if (!pickerOpen) return null;

  const tours = availableTours(user);

  return (
    <div className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/60"
        onClick={closePicker}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-picker-title"
        className="relative w-full max-w-lg my-8 bg-[var(--bg-raised)] border border-[var(--border-subtle)] rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-subtle)]">
          <div>
            <h2 id="tour-picker-title" className="text-base font-display font-bold text-[var(--text-primary)]">
              Guided walk-throughs
            </h2>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              Each one drives the real screens with live data. Use ← → or the buttons; Esc exits.
            </p>
          </div>
          <button
            ref={closeRef}
            onClick={closePicker}
            className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 space-y-1.5 max-h-[70vh] overflow-y-auto">
          {tours.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-[var(--text-tertiary)]">
              No walk-throughs are available for your access level yet.
            </p>
          ) : (
            tours.map((tour) => {
              const count = visibleSteps(tour, user).length;
              const resume = resumeStep(tour.id, count);
              return (
                <button
                  key={tour.id}
                  onClick={() => startTour(tour.id)}
                  className="w-full flex items-start gap-3 px-3 py-3 rounded-xl text-left hover:bg-[var(--bg-overlay)] border border-transparent hover:border-amber-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors"
                >
                  {/* SPEC-509: the tour's B mark, 20px in a 32px tile. Decorative — the title
                      beside it is the button's accessible name. */}
                  <span
                    className="w-8 h-8 rounded-lg grid place-items-center shrink-0 bg-[var(--bg-overlay)] text-[var(--text-primary)] shadow-[inset_0_0_0_1px_var(--border-subtle)]"
                    aria-hidden="true"
                  >
                    <TourGlyph id={tour.glyph} size={20} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">{tour.title}</span>
                      <span className="text-[10px] font-mono text-[var(--text-tertiary)]">
                        {count} step{count === 1 ? '' : 's'}
                      </span>
                      {resume > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25">
                          resume at {resume + 1}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-tertiary)] mt-0.5 leading-snug">{tour.audience}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
